/**
 * Smart Auto-Scale System
 * Detects browser/system zoom and adjusts website scale to always look optimal
 * - At 100% browser zoom → Apply 125% scale for better appearance
 * - At 125% browser zoom → Apply 100% scale (no additional scaling)
 * - At other zoom levels → Adjust proportionally to maintain optimal appearance
 */

class AutoScale {
  constructor() {
    this.targetAppearance = 1.25; // Target appearance (125%)
    this.currentBrowserZoom = 1;
    this.appliedScale = 1;
    this.isEnabled = true;
    this.init();
  }

  init() {
    this.detectBrowserZoom();
    this.applySmartScale();
    this.setupListeners();
  }

  detectBrowserZoom() {
    // Method 1: Using devicePixelRatio (most reliable)
    let detectedZoom = window.devicePixelRatio || 1;
    
    // Method 2: Using window.outerWidth vs window.innerWidth (backup)
    const screenZoom = window.screen.width / window.innerWidth;
    
    // Method 3: Using CSS media queries for more accurate detection
    const mediaZoom = this.detectZoomViaMedia();
    
    // Normalize common zoom levels
    if (detectedZoom >= 1.2 && detectedZoom <= 1.3) {
      detectedZoom = 1.25; // 125%
    } else if (detectedZoom >= 1.45 && detectedZoom <= 1.55) {
      detectedZoom = 1.5; // 150%
    } else if (detectedZoom >= 0.75 && detectedZoom <= 0.85) {
      detectedZoom = 0.8; // 80%
    } else if (detectedZoom >= 0.9 && detectedZoom <= 1.1) {
      detectedZoom = 1.0; // 100%
    }
    
    this.currentBrowserZoom = detectedZoom;
    
    console.log(`🔍 Browser zoom detected: ${Math.round(this.currentBrowserZoom * 100)}%`);
    return this.currentBrowserZoom;
  }

  detectZoomViaMedia() {
    // Create a test element to detect zoom via CSS
    const testElement = document.createElement('div');
    testElement.style.width = '1in';
    testElement.style.position = 'absolute';
    testElement.style.left = '-1000px';
    testElement.style.top = '-1000px';
    document.body.appendChild(testElement);
    
    const dpi = testElement.offsetWidth * 96;
    document.body.removeChild(testElement);
    
    return dpi / 96;
  }

  applySmartScale() {
    if (!this.isEnabled) return;

    // Smart scaling logic:
    // - If browser is at 100% → Apply 125% scale (makes it look better)
    // - If browser is at 125% → Apply 100% scale (already looks good)
    // - If browser is at other levels → Calculate to maintain optimal appearance
    
    let calculatedScale;
    
    if (this.currentBrowserZoom === 1.0) {
      // Browser at 100% → Apply 125% scale for better appearance
      calculatedScale = 1.25;
    } else if (this.currentBrowserZoom === 1.25) {
      // Browser at 125% → Apply 100% scale (no additional scaling needed)
      calculatedScale = 1.0;
    } else {
      // Other zoom levels → Calculate proportional scale to achieve optimal appearance
      calculatedScale = this.targetAppearance / this.currentBrowserZoom;
      
      // Clamp the scale to reasonable bounds
      calculatedScale = Math.max(0.8, Math.min(2.0, calculatedScale));
    }
    
    this.appliedScale = calculatedScale;
    
    // Apply the scaling using CSS custom properties
    document.documentElement.style.setProperty('--auto-scale', calculatedScale.toString());
    document.documentElement.style.setProperty('--auto-scale-compensation', `${(100 / calculatedScale).toFixed(2)}%`);
    
    // Log the scaling decision
    const finalAppearance = this.currentBrowserZoom * calculatedScale;
    console.log(`📏 Smart Scale Applied:`);
    console.log(`   Browser Zoom: ${Math.round(this.currentBrowserZoom * 100)}%`);
    console.log(`   Applied Scale: ${Math.round(calculatedScale * 100)}%`);
    console.log(`   Final Appearance: ${Math.round(finalAppearance * 100)}%`);
    
    // Show user-friendly message
    if (this.currentBrowserZoom === 1.0) {
      console.log(`✨ Enhanced to 125% for better appearance`);
    } else if (this.currentBrowserZoom === 1.25) {
      console.log(`✅ Perfect! No additional scaling needed`);
    } else {
      console.log(`🎯 Adjusted for optimal appearance`);
    }
  }

  setupListeners() {
    // Listen for zoom changes
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        const oldZoom = this.currentBrowserZoom;
        this.detectBrowserZoom();
        
        // Only reapply if zoom changed significantly
        if (Math.abs(oldZoom - this.currentBrowserZoom) > 0.1) {
          console.log(`🔄 Zoom change detected, recalculating...`);
          this.applySmartScale();
        }
      }, 250);
    });

    // Listen for orientation changes (mobile)
    window.addEventListener('orientationchange', () => {
      setTimeout(() => {
        console.log(`📱 Orientation changed, recalculating...`);
        this.detectBrowserZoom();
        this.applySmartScale();
      }, 300);
    });

    // Listen for visibility changes (when switching tabs)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        setTimeout(() => {
          this.detectBrowserZoom();
          this.applySmartScale();
        }, 100);
      }
    });

    // Listen for focus events (when user returns to tab)
    window.addEventListener('focus', () => {
      setTimeout(() => {
        this.detectBrowserZoom();
        this.applySmartScale();
      }, 100);
    });
  }

  // Public methods for manual control
  setTargetAppearance(appearance) {
    this.targetAppearance = appearance;
    this.applySmartScale();
    console.log(`🎯 Target appearance set to: ${Math.round(appearance * 100)}%`);
  }

  getCurrentScale() {
    return this.appliedScale;
  }

  getBrowserZoom() {
    return this.currentBrowserZoom;
  }

  getFinalAppearance() {
    return this.currentBrowserZoom * this.appliedScale;
  }

  disable() {
    this.isEnabled = false;
    document.documentElement.style.setProperty('--auto-scale', '1');
    document.documentElement.style.setProperty('--auto-scale-compensation', '100%');
    console.log(`❌ Smart auto-scale disabled`);
  }

  enable() {
    this.isEnabled = true;
    this.applySmartScale();
    console.log(`✅ Smart auto-scale enabled`);
  }

  toggle() {
    if (this.isEnabled) {
      this.disable();
    } else {
      this.enable();
    }
    return this.isEnabled;
  }

  // Get detailed status
  getStatus() {
    return {
      enabled: this.isEnabled,
      browserZoom: this.currentBrowserZoom,
      appliedScale: this.appliedScale,
      finalAppearance: this.getFinalAppearance(),
      targetAppearance: this.targetAppearance
    };
  }

  // Force recalculation
  recalculate() {
    console.log(`🔄 Force recalculating zoom detection...`);
    this.detectBrowserZoom();
    this.applySmartScale();
    
    // Also trigger viewport recalculation
    setTimeout(() => {
      if (window.smartViewportDetection) {
        window.smartViewportDetection.recalculate();
      }
    }, 100);
  }
}

// Create and export singleton instance
const autoScale = new AutoScale();

// Make it available globally for easy access in browser console
if (typeof window !== 'undefined') {
  window.autoScale = autoScale;
  
  // Also import and connect scaleControl
  import('./scaleControl.js').then(module => {
    const scaleControl = module.default;
    scaleControl.init(autoScale);
    window.scaleControl = scaleControl;
  });
}

export default autoScale;